import { Avatar, Box, Divider, Typography, useTheme } from "@mui/material";
import visitImg from '../../components/Logo/visit.png';
import { useNavigate } from "react-router-dom";
import { useGetSubjectDetailQuery, useGetTrialBookingSlotsQuery } from "../../store/slices/apiSlice";
import { useMemo } from "react";
import moment from "moment";
import _ from "lodash";
import { useSelector } from 'react-redux';
import EventBusyRoundedIcon from '@mui/icons-material/EventBusyRounded';
import BeenhereOutlinedIcon from '@mui/icons-material/BeenhereOutlined';
import { getEligibleVisitStep, isEnrollmentCompleted } from '../../util/subjectBookingUtils';

const SlotBooking = (props) => {
  const navigate = useNavigate();
  const subjectMasterId = useSelector(
    (state) => state?.subject?.dashboardDetails?.crfDetail?.subjectMasterId
  );
  useGetSubjectDetailQuery(subjectMasterId, { skip: !subjectMasterId });
  const theme = useTheme()
  const { data: trialBookingSlots } = useGetTrialBookingSlotsQuery();
  const crfDetails = useSelector(state => state.subject?.dashboardDetails?.crfDetail)
  const subjectDetailWithSteps = useSelector((state) => state?.subject?.details);
  const enrollmentCompleted = useMemo(
    () => isEnrollmentCompleted(subjectDetailWithSteps?.stepStatus),
    [subjectDetailWithSteps?.stepStatus]
  );
  
  const visitSlotConfig = useMemo(() => {
    if (trialBookingSlots && enrollmentCompleted && crfDetails) {
      const currentDate = new Date();
      const elegibleStep = getEligibleVisitStep(crfDetails.stepStatus);
      if (!elegibleStep) {
        return { elegibleStep: { stepLabel: 'Visits Completed' } };
      }
      const _filteredSlotsByVisit = _.filter(
        trialBookingSlots,
        (_slt) => {
          const slotStartTime = new Date(`${_slt.date.split('T')[0]}T${_slt.startTime}`)
          if (slotStartTime > currentDate && _slt.visit?.stepKey === elegibleStep?.stepKey) {
            return true;
          }
          return false;
        }
      );
      const _grpByDateSlots = _.groupBy(_filteredSlotsByVisit, (_slt) => moment(_slt.date).format('MM/DD/YYYY'));
      return { _slots: _grpByDateSlots, elegibleStep };
    } else {
      return {};
    }
  }, [trialBookingSlots, crfDetails, enrollmentCompleted]);

  const confirmVisit = (slot) => {
    navigate(`confirm/${slot.id}`);
  }

  const timeConverter = (time) => {
    return moment(time, 'HH:mm:ss').format('HH:mm');
  }
  return (
    <>
      <Box>
        <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
          Book an Appointment
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', columnGap: 2, paddingY: 2 }}>
          <Avatar
            src={visitImg}
            sx={{ width: 60, height: 60 }} // Customize the size as needed
          />
          <Typography sx={{ fontSize: 20, fontWeight: 600 }}>
            {enrollmentCompleted
              ? visitSlotConfig?.elegibleStep?.stepLabel || 'Loading visit details...'
              : 'Enrollment/Consent is pending.'}
          </Typography>
        </Box>
        <Divider />
        {!enrollmentCompleted && (
          <Box sx={{ paddingTop: 2, height: '60%' }}>
            <Box
              sx={{
                display: 'flex',
                columnGap: 1,
                margin: 1.5,
                padding: 2,
                justifyContent: 'center',
                alignItems: 'center',
                flexDirection: 'column',
              }}
            >
              <EventBusyRoundedIcon sx={{ height: 100, width: 100, color: '#FF9933' }} />
              <Typography sx={{ color: theme.palette.grey[700], fontSize: 18, pt: 3 }}>
                Enrollment/Consent is pending. 
              </Typography>
              <Typography sx={{ color: theme.palette.grey[700], fontSize: 18, pt: 3 }}>
                Please wait for the enrollment to be completed to book an appointment.
              </Typography>
            </Box>
          </Box>
        )}
        {enrollmentCompleted &&
          visitSlotConfig._slots &&
          Object.keys(visitSlotConfig._slots).length === 0 &&
          visitSlotConfig.elegibleStep?.stepLabel !== 'Visits Completed' && (
            <Box key={`dd`} sx={{ paddingTop: 2, height: '60%' }}>
              <Box
                sx={{
                  display: 'flex',
                  columnGap: 1,
                  margin: 1.5,
                  padding: 2,
                  justifyContent: 'center',
                  alignItems: 'center',
                  flexDirection: 'column',
                }}
              >
                <EventBusyRoundedIcon sx={{ height: 100, width: 100, color: '#FF9933' }} />
                <Typography sx={{ color: theme.palette.grey[700], fontSize: 18, pt: 3 }}>
                  Appointment Slots are not available.
                </Typography>
              </Box>
            </Box>
          )}
        {enrollmentCompleted &&
          !visitSlotConfig._slots &&
          visitSlotConfig.elegibleStep?.stepLabel === 'Visits Completed' && (
          <Box key={`dd`} sx={{ paddingTop: 2, height: '60%' }}>
            <Box
              sx={{
                display: 'flex',
                columnGap: 1,
                margin: 1.5,
                padding: 2,
                justifyContent: 'center',
                alignItems: 'center',
                flexDirection: 'column',
              }}
            >
              <BeenhereOutlinedIcon sx={{ height: 100, width: 100, color: theme.palette.success.main }} />
              <Typography sx={{ color: theme.palette.grey[700], fontSize: 18, pt: 3 }}>
                Study visits are completed.
              </Typography>
            </Box>
          </Box>
        )}
        {enrollmentCompleted &&
          visitSlotConfig._slots &&
          Object.keys(visitSlotConfig._slots).length > 0 &&
          Object.keys(visitSlotConfig._slots).map((key, index) => (
            <Box key={`${index}dd`} sx={{ paddingTop: 2 }}>
              <Typography
                sx={{ fontWeight: 600, textAlign: 'center', color: '#000', textDecoration: 'none', fontSize: 20 }}
              >
                {key}
              </Typography>
              {visitSlotConfig._slots[key].map((rec, index1) => (
                <Box
                  onClick={() => confirmVisit(rec)}
                  key={index1}
                  sx={{
                    display: 'flex',
                    columnGap: 1,
                    border: '1px solid #b7b4c5',
                    borderRadius: 3,
                    margin: 1.5,
                    padding: 2,
                    justifyContent: 'center',
                  }}
                >
                  <Typography sx={{ color: '#0089FF', fontSize: 20 }}>{timeConverter(rec.startTime)}</Typography>
                  <Typography sx={{ color: '#0089FF', fontSize: 20 }}>-</Typography>
                  <Typography sx={{ color: '#0089FF', fontSize: 20 }}>{timeConverter(rec.endTime)}</Typography>
                </Box>
              ))}
            </Box>
          ))}
      </Box>
    </>
  );
}

export default SlotBooking;